/* eslint-disable react/no-unknown-property */
import * as React from 'react'
import { PerspectiveCamera } from '@react-three/drei'
import { useMemo, useRef, useEffect, useCallback } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'

const SEPARATION = 100,
  AMOUNTX = 50,
  AMOUNTY = 30

const material = new THREE.ShaderMaterial({
  uniforms: {
    color: { value: new THREE.Color(1, 1, 1) },
  },
  vertexShader: `
                attribute float scale;
        
                void main() {
                    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
        
                    gl_PointSize = scale * ( 300.0 / - mvPosition.z );
        
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
  fragmentShader: `
                uniform vec3 color;
        
                void main() {
                    if ( length( gl_PointCoord - vec2( 0.5, 0.5 ) ) > 0.475 ) discard;
                    
                    gl_FragColor = vec4( color, 1.0 );
                }
                `,
})

const WaveMatrix = () => {
  const numParticles = useMemo(() => AMOUNTX * AMOUNTY, [])

  const points = useRef()
  const camera = useRef()
  const count = useRef(0)
  const scrollTop = useRef(0)

  const { positions, scales } = useMemo(() => {
    const positions = new Float32Array(numParticles * 3)
    const scales = new Float32Array(numParticles)

    let i = 0,
      j = 0
    for (let ix = 0; ix < AMOUNTX; ix++) {
      for (let iy = 0; iy < AMOUNTY; iy++) {
        positions[i] = ix * SEPARATION - (AMOUNTX * SEPARATION) / 2
        positions[i + 1] = 0
        positions[i + 2] = iy * SEPARATION - (AMOUNTY * SEPARATION) / 2

        scales[j] = 1

        i += 3
        j++
      }
    }
    return { positions, scales }
  }, [numParticles])

  useFrame(() => {
    let i = 0,
      j = 0
    for (let ix = 0; ix < AMOUNTX; ix++) {
      for (let iy = 0; iy < AMOUNTY; iy++) {
        const y =
          Math.sin((ix + count.current) * 0.3) * 50 +
          Math.sin((iy + count.current) * 0.5) * 50

        const scale =
          (Math.sin((ix + count.current) * 0.3) + 1) * 10 +
          (Math.sin((iy + count.current) * 0.5) + 1) * 10

        points.current.geometry.attributes.position.array[i + 1] = y
        points.current.geometry.attributes.scale.array[j] = scale

        i += 3
        j++
      }
    }
    count.current += 0.1
    points.current.geometry.attributes.position.needsUpdate = true
    points.current.geometry.attributes.scale.needsUpdate = true

    camera.current.position.y = 500 - scrollTop.current
    camera.current.updateProjectionMatrix()
  })

  const handleScroll = useCallback(() => {
    scrollTop.current = window.scrollY || document.documentElement.scrollTop
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return (
    <group>
      <PerspectiveCamera
        ref={camera}
        makeDefault
        position={[0, 500, 1500]}
        rotation={[-Math.PI / 8, 0, 0]}
        fov={100}
        aspect={window.innerWidth / window.innerHeight}
        near={1}
        far={10000}
      />
      <points ref={points}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={numParticles}
            array={positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-scale"
            count={numParticles}
            array={scales}
            itemSize={1}
          />
        </bufferGeometry>
        <primitive attach="material" object={material} />
      </points>
    </group>
  )
}

export default WaveMatrix
